'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from '@/providers/StoreProvider';
import { ensureSession } from '@/lib/api';
import { isProfileComplete } from '@repo/utils';

/**
 * Automatically prompts logged-in users to complete their profile (SignupDetailsStep)
 * at most once per interval, similar to the legacy DynamicCompleteProfile flow.
 */
export function ProfileCompletionPrompt() {
  const pathname = usePathname();
  const openProfileUpdate = useStore(s => s.authUI.openProfileUpdate);
  const mutedUntil = useStore(s => s.authUI.profileCompletionPromptMutedUntil);

  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const profile = useStore(s => s.profile.profile);
  const syncProfile = useStore(s => s.profile.syncProfile);
  const authIsOpen = useStore(s => s.authUI.isOpen);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isLoggedIn) return;
    // Avoid racing store rehydration/bootstrap (auth sheet can get immediately overwritten).
    if (authIsOpen) return;
    if (mutedUntil && Date.now() < mutedUntil) return;

    // Don't interrupt subscription flows
    if (pathname?.includes('subscription')) return;
    let cancelled = false;

    const run = async () => {
      if (!(await ensureSession()) || cancelled) return;
      // If profile isn't loaded yet (common on some routes), fetch it before deciding.
      const currentProfile =
        profile ?? (syncProfile ? await syncProfile().catch(() => null) : null);
      if (cancelled || !currentProfile) return;
      const isComplete = isProfileComplete(currentProfile);
      if (isComplete) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) openProfileUpdate();
        });
      });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [authIsOpen, isLoggedIn, mutedUntil, pathname, profile]);

  return null;
}
